/**
 * currency.js — shared currency formatting utilities
 *
 * formatAmount(amount, currency)
 *   Formats a number using the ISO 4217 currency code (e.g. 'INR', 'USD', 'EUR').
 *   Falls back to 'INR' when no currency is provided so existing behaviour is
 *   preserved for legacy transactions that were saved before multi-currency support.
 *
 * getCurrencySymbol(currency)
 *   Returns the symbol for a given ISO currency code (e.g. '₹', '$', '€').
 */

const LOCALE_MAP = {
  INR: 'en-IN',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  AUD: 'en-AU',
  CAD: 'en-CA',
  CHF: 'de-CH',
  SGD: 'en-SG',
  AED: 'ar-AE',
  SAR: 'ar-SA',
  MXN: 'es-MX',
  BRL: 'pt-BR',
  ZAR: 'en-ZA',
  KRW: 'ko-KR',
  HKD: 'zh-HK',
  NOK: 'nb-NO',
  SEK: 'sv-SE',
  DKK: 'da-DK',
  NZD: 'en-NZ',
  MYR: 'ms-MY',
  THB: 'th-TH',
  IDR: 'id-ID',
  PHP: 'en-PH',
  VND: 'vi-VN',
  TWD: 'zh-TW',
  PKR: 'ur-PK',
  BDT: 'bn-BD',
  NGN: 'en-NG',
  EGP: 'ar-EG',
  KES: 'en-KE',
};

// Currencies where we show 0 decimal places
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'VND', 'IDR', 'CLP', 'ISK']);

/**
 * Format a monetary amount with the correct currency symbol and locale.
 *
 * @param {number|string} amount
 * @param {string} [currency='INR']
 * @returns {string}
 */
export function formatAmount(amount, currency = 'INR') {
  const num = parseFloat(amount);
  if (isNaN(num)) return '—';

  const code = (currency || 'INR').toUpperCase();
  const locale = LOCALE_MAP[code] || 'en-US';
  const decimals = ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2;

  try {
    return num.toLocaleString(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch {
    // Fallback for unknown currency codes
    return `${code} ${num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }
}

/**
 * Short format: e.g. "₹1.2k" instead of "₹1,200.00"
 *
 * @param {number|string} amount
 * @param {string} [currency='INR']
 * @returns {string}
 */
export function formatShortAmount(amount, currency = 'INR') {
  const num = parseFloat(amount);
  if (isNaN(num)) return '—';

  const code = (currency || 'INR').toUpperCase();
  const symbol = getCurrencySymbol(code);

  if (Math.abs(num) >= 1_000_000) {
    return `${symbol}${(num / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(num) >= 1_000) {
    return `${symbol}${(num / 1_000).toFixed(1)}k`;
  }
  return `${symbol}${Math.round(num)}`;
}

/**
 * Return the currency symbol for an ISO 4217 code.
 * Uses the Intl API when available, with a built-in fallback map.
 *
 * @param {string} [currency='INR']
 * @returns {string}
 */
export function getCurrencySymbol(currency = 'INR') {
  const code = (currency || 'INR').toUpperCase();
  const locale = LOCALE_MAP[code] || 'en-US';

  try {
    const parts = (0).toLocaleString(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).split('');

    // Filter out digits and group/decimal separators to get the symbol
    const symbol = parts.filter(p => isNaN(p) && p !== ',' && p !== '.' && p !== '\u00a0' && p !== ' ').join('').trim();
    return symbol || code;
  } catch {
    return code;
  }
}
